import React from 'react';

import { DatePickerField, MCButton, MCText, SearchTextField } from 'src/components/basic';
import { TableLibrary } from 'src/components/complex';
import BaseComponent from 'src/components/BaseComponent';
import ShortInfo from 'src/components/user/ShortInfo';

import {
  addressObject,
  lang,
  permissions,
  remCss,
  remFontCss,
  scssVariables,
  withRouter
} from 'src/helpers';

import model from './model';
import AppointmentStatus from 'src/components/appointments/AppointmentStatus';
import LANG_CONST from 'src/helpers/lang/const';
import { Link } from 'react-router-dom';
import SVGIcon, { BASE_ICONS } from 'src/components/svg';
import moment from 'moment/moment';

const cellProps = {
  style: {
    alignItems: 'flex-start',
    fontSize: remFontCss(16)
  }
};

class Appointment extends BaseComponent {
  componentDidMount() {
    model.setStateSilent({ pageIndex: 0 });
    model.subscribe(this.subscription);
  }

  componentWillUnmount() {
    model.unsubscribe(this.subscription);
  }

  render() {
    let loading = model.refreshing();
    let noResults = model.noResults();
    let searchQuery = model.listInput('search');
    let items = model.items().filter((item) => !!item.data && !!item.data.startDate);
    let { total = 0 } = model.getCurrent();
    const { PAGES, ACTIONS } = permissions;

    const columns = [
      {
        label: LANG_CONST.doctors,
        resize: true,
        cellProps: { style: { alignItems: 'flex-start' } },
        renderCell: ({ doctor }) => {
          return (
            !!doctor?.id && (
              <ShortInfo
                user={doctor}
                to={permissions.profilePath(doctor)}
                vertical
                textSize={16}
              />
            )
          );
        }
      },
      {
        label: LANG_CONST.clinics,
        resize: true,
        cellProps: { style: { alignItems: 'flex-start' } },
        renderCell: ({ clinic }) => {
          return (
            !!clinic?.id && (
              <ShortInfo
                user={clinic}
                to={permissions.profilePath(clinic)}
                vertical
                textSize={16}
              />
            )
          );
        }
      },
      {
        label: LANG_CONST.date,
        resize: true,
        cellProps,
        width: `${remCss(130)}px`,
        renderCell: (item) => {
          if (!item.data?.startDate) return '';

          return (
            <MCText
              label={moment(item.data.startDate).calendar(null, {
                sameDay: '[Today] HH:mm',
                nextDay: '[Tomorrow] HH:mm',
                nextWeek: 'ddd HH:mm',
                lastDay: '[Yesterday] HH:mm',
                lastWeek: '[Last] ddd HH:mm',
                sameElse: function (now) {
                  if (this.isSame(now, 'year')) {
                    return 'MMM DD HH:mm';
                  }

                  return 'DD/MM/YYYY HH:mm';
                }
              })}
              heading
              size={16}
            />
          );
        }
      },
      {
        label: LANG_CONST.users,
        resize: true,
        cellProps: { style: { alignItems: 'flex-start' } },
        renderCell: (item) => {
          const partner = item.patient;
          return (
            partner?.id && (
              <ShortInfo
                user={partner}
                to={permissions.profilePath(partner)}
                vertical
                textSize={16}
                specialist={[]}
              />
            )
          );
        }
      },
      {
        label: LANG_CONST.diagnostic,
        resize: true,
        cellProps,
        renderCell: (item) => item.data?.disease
      },
      {
        label: LANG_CONST.location,
        resize: true,
        cellProps,
        renderCell: (item) => {
          if (item.data.appointmentType === 'virtual') return 'Virtual';
          if (!item.data.hospital) return '';

          return (
            <MCText
              numberOfLines={4}
              label={
                item.data.hospital?.split('_')[0] +
                ' ' +
                addressObject({ cityAddress: item.data.cityAddress }).fullAddressNameReverse
              }
              heading
              size={16}
            />
          );
        }
      },
      {
        label: LANG_CONST.actions,
        cellStyles: { textAlign: 'right' },
        cellProps,
        width: `${remCss(190)}px`,
        renderCell: (item) => {
          const params = { auth: model.state.user, appointment: item };
          const style = { width: remCss(120), paddingVertical: remCss(8) };

          return (
            <>
              <div className={'mc-flex mc-flex-start'}>
                <div className={'mc-flex-5-col mc-grow mc-flex-start'}>
                  {ACTIONS.APPOINTMENTS_RESCHEDULE_BUTTON.checkActive(params) && (
                    <MCButton
                      {...ACTIONS.APPOINTMENTS_RESCHEDULE_BUTTON.attributes}
                      type={'link'}
                      style={style}
                      small
                      to={PAGES.ADMIN_APPOINTMENTS_RESCHEDULE_PAGE.match(item.id)}
                    />
                  )}
                  {ACTIONS.APPOINTMENTS_REBOOK_BUTTON.checkActive(params) && (
                    <MCButton
                      {...ACTIONS.APPOINTMENTS_REBOOK_BUTTON.attributes}
                      type={'link'}
                      style={style}
                      small
                      to={PAGES.ADMIN_APPOINTMENTS_REBOOK_PAGE.match(item.id)}
                    />
                  )}
                  {!ACTIONS.APPOINTMENTS_CONFIRM_BUTTON.checkActive(params) &&
                    ACTIONS.APPOINTMENTS_CANCEL_BUTTON.checkActive(params) && (
                      <MCButton
                        {...ACTIONS.APPOINTMENTS_CANCEL_BUTTON.attributes}
                        small
                        style={style}
                        onClick={() => model.showCancel(item)}
                      />
                    )}

                  <AppointmentStatus appointment={item} style={style} small />
                </div>
                <Link
                  className={'mc-no-style-btn'}
                  to={PAGES.ADMIN_APPOINTMENTS_DETAIL_PAGE.match(item.id)}
                >
                  <SVGIcon
                    iconname={BASE_ICONS.EYE_FILL}
                    fill={scssVariables['colors-mc-primary']}
                    fillOpacity={'0.5'}
                    width={36}
                    dark
                    padding={5}
                    radius
                  />
                </Link>
              </div>
            </>
          );
        }
      }
    ];

    return (
      <div className={'mc-container-content mc-appointment'}>
        <div className={'mc-container-content__header mc-flex-15'}>
          <span className={'mc-grow'}>{lang.t(LANG_CONST.titles.appointments)}</span>
          <SearchTextField
            width={300}
            template={'2'}
            onChange={(text) => {
              model.setStateSilent({
                pageIndex: 0
              });
              model.setListStateSilent({ items: [], total: 0 }, 'search');
              model.handleChangeSearch(text);
              if (!text) {
                model.setListState({ items: [] }, model.getCurrentId(), null, true);
                model.refresh();
              }
            }}
            value={model.listInput('search')}
          />
          <DatePickerField
            style={{ maxWidth: remCss(200) }}
            styleBox={{ backgroundColor: 'white' }}
            template={'6'}
            icontype={2}
            placeholder={LANG_CONST.start_date}
            placement={'bottom-end'}
            close={false}
            name={'startDate'}
            value={model.input('startDate')}
            onChange={(n, v) => {
              if (v?.toString() === model.input('startDate')?.toString()) return;
              model.setStateSilent({
                pageIndex: 0,
                [n]: v
              });
              model.setListStateSilent({ items: [], total: 0 }, model.getCurrentId());
              model.refresh();
            }}
            invalid={{}}
          />
          <DatePickerField
            style={{ maxWidth: remCss(200) }}
            styleBox={{ backgroundColor: 'white' }}
            template={'6'}
            icontype={2}
            close={false}
            placeholder={LANG_CONST.end_date}
            placement={'bottom-end'}
            name={'endDate'}
            value={model.input('endDate')}
            onChange={(n, v) => {
              if (v?.toString() === model.input('endDate')?.toString()) return;
              model.setStateSilent({
                pageIndex: 0,
                [n]: v
              });
              model.setListStateSilent({ items: [], total: 0 }, model.getCurrentId());
              model.refresh();
            }}
            invalid={{}}
          />
          <MCButton
            onClick={() => {
              this.navigate(PAGES.ADMIN_APPOINTMENTS_CREATE_PAGE.path);
            }}
            primary
            medium
            style={{ height: remCss(50) }}
            label={LANG_CONST.buttons.add_appointment}
            radius
            light
          />
        </div>
        <div className={'mc-container-content__body'}>
          <div className={'mc-container-content__body-content'} style={{ marginTop: remCss(25) }}>
            <TableLibrary
              loading={loading}
              noResults={noResults}
              searchQuery={searchQuery}
              sortBy={model.state.sortBy}
              total={total}
              limit={model.listLimit}
              selectedPage={model.state.pageIndex}
              onSortChange={(sortBy) => {
                model.handleChange('sortBy', sortBy);
                model.setListState({ items: [] }, model.getCurrentId(), null, true);
                model.loadMore();
              }}
              onSetPage={(v) => {
                model.handleChange('pageIndex', v);
                model.setListState({ items: [] }, model.getCurrentId(), null, true);
                model.loadMore();
              }}
              columns={columns}
              data={items}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(Appointment);
